// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Insert shapes, as outlines in playfield coordinates (M3-15).
//
// A machine states a lamp as a shape name, a position, a size or a radius and a rotation. This
// turns that into an outline, in metres, in PH-001's frame — and stops there, because the
// interesting part is the geometry and the boring part is filling it in.
//
// Keeping them separate is what makes them testable. An insert drawn a centimetre from where
// its switch is looks like art, not like a bug: the shot still works, the lamp still lights,
// and it is simply in slightly the wrong place, forever. So the outlines are asserted against
// the devices they belong to instead.
//
// Every shape is returned as a closed polygon rather than as a description. One consumer, one
// representation, no `switch` in the rasteriser.

import type { MachineLamp } from '@core/machine'

export type Point = readonly [number, number]

/** Segments used to approximate a full circle. A 9 mm insert is a few pixels of texture. */
const CIRCLE_SEGMENTS = 24

/**
 * The outline of one lamp's insert, or nothing if it has no drawable shape.
 *
 * A GI lamp lights a *region* of the playfield and has no insert at all; a lamp with no
 * position cannot be placed. Both answer `undefined` rather than throwing, because a machine
 * is entitled to both and the caller reports them rather than failing to load.
 */
export function lampOutline(lamp: MachineLamp): Point[] | undefined {
  const at = lamp.position
  if (at === undefined) return undefined

  const rotation = lamp.rotation ?? 0
  const shape = lamp.shape ?? 'round'
  const radius = lamp.radius ?? 0.009
  const size: Point = lamp.size ?? [radius * 2, radius * 2]
  const hx = size[0] / 2
  const hy = size[1] / 2

  const local = ((): Point[] => {
    switch (shape) {
      case 'round':
        return ellipse(radius, radius)
      case 'oval':
        return ellipse(hx, hy)
      case 'rect':
        return [
          [-hx, -hy],
          [hx, -hy],
          [hx, hy],
          [-hx, hy],
        ]
      case 'triangle':
        return [
          [0, hy],
          [hx, -hy],
          [-hx, -hy],
        ]
      case 'arrow':
        // A pinball arrow: a broad head on a narrower shaft, pointing along +y before
        // rotation, because "this way" on a playfield means "up the playfield" by default.
        return [
          [0, hy],
          [hx, hy * 0.1],
          [hx * 0.45, hy * 0.1],
          [hx * 0.45, -hy],
          [-hx * 0.45, -hy],
          [-hx * 0.45, hy * 0.1],
          [-hx, hy * 0.1],
        ]
      case 'star':
        return star(hx, hy)
      default:
        return ellipse(radius, radius)
    }
  })()

  const cos = Math.cos(rotation)
  const sin = Math.sin(rotation)
  return local.map(([x, y]) => [
    at[0] + x * cos - y * sin,
    at[1] + x * sin + y * cos,
  ])
}

function ellipse(rx: number, ry: number): Point[] {
  const out: Point[] = []
  for (let i = 0; i < CIRCLE_SEGMENTS; i++) {
    const a = (2 * Math.PI * i) / CIRCLE_SEGMENTS
    out.push([rx * Math.cos(a), ry * Math.sin(a)])
  }
  return out
}

/** A five-pointed star, points out, first point up. */
function star(rx: number, ry: number): Point[] {
  const out: Point[] = []
  for (let i = 0; i < 10; i++) {
    const a = Math.PI / 2 + (Math.PI * i) / 5
    const scale = i % 2 === 0 ? 1 : 0.42
    out.push([rx * scale * Math.cos(a), ry * scale * Math.sin(a)])
  }
  return out
}

/**
 * Playfield metres to normalised texture coordinates, `y` up.
 *
 * The same convention the playfield's art program uses (see `artTexture.ts`): `y` runs *up*
 * the playfield, so a lamp at the far end is near 1. The insert map is sampled by the same
 * UVs as the art, so the two have to agree — and the way they stop agreeing is a whole set of
 * inserts appearing at the player's end, mirrored, which reads as an art mistake.
 */
export function toTextureUv(
  point: Point,
  width: number,
  length: number,
): Point {
  return [(point[0] + width / 2) / width, (point[1] + length / 2) / length]
}
